<template>
  <v-off-canvas
    id="template-create-offcanvas-v2"
    :backdrop-static="false"
    :offcanvas-class="['offcanvas-end', 'activity-create-offcanvas', { 'double-offcanvas': isDoubleOffCanvas }]"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header class="activity-create-header">
        <div>
          <h5 class="offcanvas-title mb-4">{{ $t("clientTour.createNewProduct") }}</h5>
          <p class="activity-create-subtitle mb-0">{{ $t("template.createSubtitle") }}</p>
        </div>
      </off-canvas-header>

      <off-canvas-body class="offcanvas-body-visible activity-create-body">
        <form id="template-create-form" class="activity-create-form" @submit.prevent="createTemplate">
          <section class="activity-create-section">
            <header class="activity-create-section__header">
              <span class="activity-create-section__icon">
                <v-icon icon="pencil" size="xs" />
              </span>
              <div>
                <h6 class="activity-create-section__title">{{ $t("template.createSectionBasics") }}</h6>
                <p class="activity-create-section__subtitle">{{ $t("clientTour.basicInformation") }}</p>
              </div>
            </header>

            <div class="row g-12">
              <div class="col-12 col-md-8">
                <v-form-input
                  id="template-create-name"
                  v-model="name"
                  :label="`${$t('general.shared.name')}*`"
                  :yup-errors-variable="nameError"
                  autocomplete="off"
                  form-type="outline"
                />
              </div>
              <div class="col-12 col-md-4">
                <v-form-input
                  id="template-create-total-days"
                  v-model="totalDays"
                  :label="`${$t('general.shared.totalDays')}*`"
                  :yup-errors-variable="totalDaysError"
                  autocomplete="off"
                  form-type="outline"
                  type="number"
                  min="1"
                />
              </div>
            </div>
          </section>

          <section class="activity-create-section">
            <header class="activity-create-section__header">
              <span class="activity-create-section__icon">
                <v-icon icon="compass" size="xs" />
              </span>
              <div>
                <h6 class="activity-create-section__title">{{ $t("template.createSectionTrip") }}</h6>
                <p class="activity-create-section__subtitle">{{ $t("clientTour.howWillYourPath") }}</p>
              </div>
            </header>

            <v-form
              id="template-create-travel-type-select"
              :label="`${$t('template.travelType')}*`"
              :yup-errors-variable="travelTypeError"
              form-type="outline"
              class="mb-12"
            >
              <v-select
                v-model="travelType"
                :clearable="false"
                :options="travelTypes"
                :reduce="(returnValue) => returnValue.key"
              >
                <template v-slot:no-options>
                  <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                </template>
              </v-select>
            </v-form>

            <div class="activity-create-field-label">{{ $t("template.modality") }}*</div>
            <div class="modality-picker" role="radiogroup" :aria-label="$t('template.modality')">
              <button
                v-for="option in modalityOptions"
                :key="option.key"
                type="button"
                class="modality-picker__option"
                :class="{ 'modality-picker__option--active': modality === option.key }"
                :aria-pressed="modality === option.key"
                @click="modality = option.key"
              >
                <span class="modality-picker__option-icon" :class="`modality-picker__option-icon--${option.key}`">
                  <v-icon :icon="option.key === 'group' ? 'group' : 'user'" size="sm" />
                </span>
                <span class="modality-picker__option-content">
                  <span class="modality-picker__option-title">{{ option.label }}</span>
                  <span class="modality-picker__option-desc">
                    {{
                      option.key === "group"
                        ? $t("template.modalityGroupDesc")
                        : $t("template.modalitySelfGuidedDesc")
                    }}
                  </span>
                </span>
                <span v-if="modality === option.key" class="modality-picker__check">
                  <v-icon icon="check" size="xs" />
                </span>
              </button>
            </div>
          </section>

          <section class="activity-create-section">
            <header class="activity-create-section__header">
              <span class="activity-create-section__icon">
                <v-icon icon="map" size="xs" />
              </span>
              <div>
                <h6 class="activity-create-section__title">{{ $t("template.createSectionRoute") }}</h6>
                <p class="activity-create-section__subtitle">{{ $t("general.shared.origin") }} → {{ $t("general.shared.destination") }}</p>
              </div>
            </header>

            <div class="route-fields">
              <v-input-address
                id="template-create-origin-search"
                ref="template-create-origin-search-ref"
                :get-address="true"
                :get-radius-bounds="true"
                :label="$t('general.shared.origin')"
                :radius-bounds="20000"
                class="route-fields__input"
                @address="createLocation($event, true)"
              />

              <div class="route-fields__arrow" aria-hidden="true">
                <v-icon icon="arrow-right" size="sm" />
              </div>

              <v-input-address
                id="template-create-destination-search"
                ref="template-create-destination-search-ref"
                :get-address="true"
                :get-radius-bounds="true"
                :label="$t('general.shared.destination')"
                :radius-bounds="20000"
                class="route-fields__input"
                @address="createLocation($event, false)"
              />
            </div>
          </section>

          <section class="activity-create-section">
            <header class="activity-create-section__header">
              <span class="activity-create-section__icon">
                <v-icon icon="picture" size="xs" />
              </span>
              <div>
                <h6 class="activity-create-section__title">{{ $t("general.shared.media") }}</h6>
                <p class="activity-create-section__subtitle">{{ $t("template.createMediaOptional") }}</p>
              </div>
            </header>

            <v-uploader ref="uploader" :after-upload="onChange" route-prefix="api/s3" @removed="onChange" />
          </section>

          <footer class="activity-create-footer">
            <v-button
              :disabled="!meta.valid || disableButton"
              :is-loading="accepted"
              class="activity-create-footer__button btn-icon w-100"
              size="sm"
              type="submit"
              variant="primary"
            >
              {{ $t("general.button.create") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </footer>
        </form>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import { VForm, VFormInput } from "@uniqoders/form";
import { useI18n } from "vue-i18n";
import vSelect from "vue-select";
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useAuthStore } from "@/stores/auth";
import api from "@/api";
import ClientTour from "@/api/objects/ClientTour";
import Template from "@/api/objects/Template";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { buildModalitySelectOptions } from "@/helpers/ModalityHelper";
import useConcepts from "@/helpers/Concepts";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import VUploader from "@/components/vendor/upload/VUploader.vue";

defineProps({
  templateData: {
    type: Object as () => Template,
    required: false,
    default: null,
  },
  clientTourData: {
    type: Object as () => ClientTour,
    required: false,
    default: null,
  },
  isDoubleOffCanvas: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["closed", "reloadTemplates"]);

const router = useRouter();
const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  name: yup.string().required().label(t("general.shared.name")),
  totalDays: yup
    .number()
    .required()
    .min(1)
    .transform((value) => (isNaN(value) ? undefined : value))
    .label(t("general.shared.daysNumber")),
  travelType: yup.string().required().label(t("template.travelType")),
  modality: yup.string().required().oneOf(["self-guided", "group"]).label(t("template.modality")),
});

const initialValues = {
  totalDays: 1,
  modality: "self-guided",
};

const form = useFormValidation(rules, initialValues);
const { value: name, errorMessage: nameError } = useField<string>("name");
const { value: totalDays, errorMessage: totalDaysError } = useField<number>("totalDays");
const { value: travelType, errorMessage: travelTypeError } = useField<string>("travelType");
const { value: modality } = useField<string>("modality");
const { accepted, meta, toggleAccepted } = form;

const concepts = useConcepts();
const travelTypes = computed(() => {
  const conceptList = concepts.concepts.value?.["tour.travel_type"];
  return conceptList ? mapConcepts(conceptList) : [];
});

const modalityOptions = computed(() => buildModalitySelectOptions(t));

const authStore = useAuthStore();
const disableButton = ref(false);
const origin = ref<number | null>(null);
const destination = ref<number | null>(null);
const uploader = ref<{ mediaLibrary: { state: { media: unknown[] } } } | null>(null);
const media = ref<any[]>([]);

const mediaUuids = computed(() => {
  return media.value.map((mediaItem: any) => ({
    uuid: mediaItem.attributes.uuid,
    name: mediaItem.attributes.name,
    collection: "activity_image",
  }));
});

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media as any[];
  }
};

const createLocation = async (address: Record<string, any>, isOrigin = true) => {
  try {
    const location: Location = await api.location.create({
      name: address.name,
      provider_id: address.placeId,
      lat: address.lat,
      lng: address.lng,
      country: address.country.longName,
      province: address.regionProvince.longName,
      state: address.stateCommunity.longName,
    });

    if (isOrigin) {
      origin.value = Number(location.id);
    } else {
      destination.value = Number(location.id);
    }
  } catch (error) {
    console.error("Error creating location:", error);
  }
};

const createTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data: Record<string, unknown> = {
      name: name.value,
      total_days: totalDays.value,
      travel_type: travelType.value,
      modality: modality.value,
      origin: origin.value,
      destination: destination.value,
      is_published: true,
    };

    if (authStore.agency) {
      const response = await api.activityService.createEmptyActivity(authStore.agency.slug, data);

      if (response && mediaUuids.value.length > 0) {
        const payload = {
          media: mediaUuids.value,
          model_type: "activity",
          collection: "activity_image",
          model_id: response.id,
        };

        await api.media.confirm("s3", payload);
      }

      toast.success(t("general.shared.savedChanges"), {
        position: "bottom-right",
      });
      emit("reloadTemplates");
      await router.replace({
        name: "activities.show",
        params: { activitiesId: response.id },
      });
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  } finally {
    toggleAccepted();
    disableButton.value = false;
  }
};
</script>

<style scoped lang="scss">
.offcanvas-body-visible {
  overflow: visible !important;
}

.activity-create-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 1rem;
}

.activity-create-subtitle {
  font-size: 0.8125rem;
  color: #6c757d;
  line-height: 1.45;
}

.activity-create-body {
  padding-bottom: 0 !important;
}

.activity-create-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 1rem;
}

.activity-create-section {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem 1.125rem;

  &--compact {
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }
}

.activity-create-section__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.activity-create-section__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  background: rgba(230, 18, 117, 0.08);
  color: #e61275;
  flex-shrink: 0;
}

.activity-create-section__title {
  font-size: 0.9375rem;
  font-weight: 700;
  margin: 0 0 0.125rem;
  color: #2c2b2b;
}

.activity-create-section__subtitle {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

.activity-create-field-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2c2b2b;
  margin-bottom: 0.5rem;
}

.modality-picker {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.625rem;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
}

.modality-picker__option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: #fafafa;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba(230, 18, 117, 0.35);
    background: #fff;
  }

  &--active {
    border-color: #e61275;
    background: rgba(230, 18, 117, 0.04);
    box-shadow: 0 0 0 1px rgba(230, 18, 117, 0.12);
  }
}

.modality-picker__option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 8px;
  flex-shrink: 0;

  &--self-guided {
    background: #e3f2fd;
    color: #1565c0;
  }

  &--group {
    background: rgba(238, 42, 123, 0.12);
    color: #ad1457;
  }
}

.modality-picker__option-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
}

.modality-picker__option-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #2c2b2b;
}

.modality-picker__option-desc {
  font-size: 0.6875rem;
  color: #6c757d;
  line-height: 1.35;
}

.modality-picker__check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #e61275;
  color: #fff;
  flex-shrink: 0;
}

.route-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.route-fields__input {
  width: 100%;
}

.route-fields__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  padding: 0.125rem 0;
}

@media (min-width: 768px) {
  .route-fields {
    flex-direction: row;
    align-items: flex-end;
    gap: 0.75rem;
  }

  .route-fields__input {
    flex: 1;
    min-width: 0;
  }

  .route-fields__arrow {
    padding-bottom: 0.75rem;
  }
}

.activity-create-footer {
  position: sticky;
  bottom: 0;
  margin-top: 0.5rem;
  padding: 1rem 0 0.5rem;
  background: linear-gradient(to top, #fff 80%, rgba(255, 255, 255, 0));
  z-index: 2;
}

.activity-create-footer__button {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 10px !important;
  font-weight: 700;
  padding-top: 0.625rem !important;
  padding-bottom: 0.625rem !important;
}
</style>
